import { useParams } from "react-router-dom";
import {
  BRAND_ARTIFACT_META,
  buildBrandSettingsPath,
  isBrandArtifactType,
} from "../lib/brand-navigation";
import { useActiveBrand } from "../hooks/useActiveBrand";
import { useBrand } from "../hooks/useBrand";
import { StrategyWorkspaceSurface } from "../components/strategy/StrategyWorkspaceSurface";
import { VoiceWorkspaceSurface } from "../components/voice/VoiceWorkspaceSurface";
import { ArtifactRouteRecovery } from "../components/brand/ArtifactRouteRecovery";
import { workObjectDisplaySpec } from "../lib/work-object-display";

export function BrandArtifactWorkspace() {
  const { brandId, artifactType } = useParams<{ brandId: string; artifactType: string }>();
  const { activeBrand, isLoading } = useActiveBrand();
  const { data: brandDetail, isLoading: isBrandLoading, isError } = useBrand(brandId);

  if (!brandId || !artifactType) {
    return (
      <ArtifactRouteRecovery
        eyebrow="Brand artifact"
        title="This artifact route is incomplete"
        message="The link is missing the brand or artifact context needed to open a workspace."
        primaryAction={{ href: "/brand", label: "Choose a brand" }}
        secondaryAction={{ href: "/home", label: "Back to workspace" }}
      />
    );
  }

  if (!isBrandArtifactType(artifactType)) {
    return (
      <ArtifactRouteRecovery
        eyebrow="Brand artifact"
        title="This artifact type is not supported"
        message="The link points to an artifact route that HelloConvo does not currently expose. Return to brand settings and open Strategy or Voice from a supported entry point."
        primaryAction={{ href: buildBrandSettingsPath(brandId, "profile"), label: "Back to brand settings" }}
        secondaryAction={{ href: `/brand/${encodeURIComponent(brandId)}`, label: "Open brand home" }}
      />
    );
  }

  const brand = brandDetail?.data ?? (activeBrand?.id === brandId ? activeBrand : undefined);

  if (!brand && (isLoading || isBrandLoading)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (isError && !brand) {
    return (
      <ArtifactRouteRecovery
        eyebrow="Brand artifact"
        title="This brand artifact could not be opened"
        message="The brand may have been removed, you may no longer have access, or the saved link is stale. Choose another safe surface instead of staying on a dead-end route."
        primaryAction={{ href: "/brand", label: "Choose a brand" }}
        secondaryAction={{ href: "/home", label: "Back to workspace" }}
      />
    );
  }

  if (!brand) {
    return (
      <ArtifactRouteRecovery
        eyebrow="Brand artifact"
        title="This brand is no longer available"
        message="The artifact link no longer resolves to an accessible brand. Pick another brand or return to the dashboard."
        primaryAction={{ href: "/brand", label: "Choose a brand" }}
        secondaryAction={{ href: "/home", label: "Back to workspace" }}
      />
    );
  }

  const surface = artifactType === "voice"
    ? (
      <VoiceWorkspaceSurface
        brandId={brandId}
        brandName={brand.name}
        locationLabel="Workspace / Voice artifact"
      />
    )
    : artifactType === "strategy"
    ? (
      <StrategyWorkspaceSurface
        brandId={brandId}
        brandName={brand.name}
        locationLabel="Workspace / Strategy artifact"
      />
    )
    : null;
  const { settingsSection, workspaceTitle } = BRAND_ARTIFACT_META[artifactType];
  const displaySpec = workObjectDisplaySpec(
    artifactType === "voice" ? "brand_voice" : "strategy_artifact",
    "unknown",
  );

  return (
    <div className="mx-auto max-w-6xl space-y-4 px-4 py-4 md:px-6">
      <section className="rounded-[20px] border border-slate-200 bg-white px-5 py-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
              Brand artifact · {displaySpec.typeLabel}
            </p>
            <h1 className="mt-1 text-2xl font-bold text-slate-900">{workspaceTitle}</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Read the canonical {settingsSection} document first, then enter edit mode only when you want to revise the saved brand guidance.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
              {brand.name}
            </span>
            <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
              {displaySpec.primitive.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      </section>

      {surface}
    </div>
  );
}
